import { useMemo, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Input,
  CheckboxGroup,
  Checkbox,
  Textarea,
  FormLabel,
} from "@chakra-ui/react";
import { ErrorMessage, ErrorSpacer, RequiredAsterisk } from "./styles";
import { InputV2, SelectV2 } from "../../../Reusable";
import RadioButtonGroup from "../../../Components/RadioButtonGroup/RadioButtonGroup";
import CitiesAndStates from "../../../Components/CitiesAndStates/CitiesAndStates";
import { useGetOrganizationsQuery } from "../../../../redux/reducers/apiSlice";

const StepGeneralInfo = ({ formik, editMode }) => {
  const type = formik.values.type;
  const FOUNDATION_TYPE = "foundation";
  const GRASSROOTS_TYPE = "grassroots";
  const NON_PROFIT_TYPE = "non-profit";
  const CORPORATION_TYPE = "corporation";


  useEffect(() => {
         async function fetchAddress({ lat, lon }: { lat: number; lon: number }) {
          try {
            const res = await fetch(
              `/api/nominatim/reverse?format=jsonv2&lat=${lat}&lon=${lon}`
            );
            if (!res.ok) throw new Error('Reverse geocoding failed');
            const data = await res.json();
            console.log("\n\nDATA: ",data, "\n\n");
            // setAddress(data.address);
          } catch (err) {
            
          }
        }

        const lon = formik.values.location.longitude;
        const lat = formik.values.location.longitude;

        if(lon && lat){
          fetchAddress({ lon, lat })
        }

  },[formik.values.location.latitude, formik.values.location.longitude])

  const { data: organizations = [], isLoading: isLoadingFoundations } =
    useGetOrganizationsQuery();

  const foundationOptions = useMemo(() => {
    return (organizations || [])
      .filter((org) => {
        const orgType = org.type?.toLowerCase();
        return orgType === FOUNDATION_TYPE || orgType === "Foundation";
      })
      .map((org) => ({
        label: org.name,
        value: org.id,
      }));
  }, [organizations]);

  const showFundedBy = type !== FOUNDATION_TYPE;

  return (
    <div className="step-general-info">
      <Box mb="32px">
        <Text
          fontSize="11px"
          color="#000"
          mb="12px"
          data-cy="cypress-required-texts"
        >
          All fields marked with ( <RequiredAsterisk /> ) are required
        </Text>
        <SimpleGrid columns={{ sm: 1, md: 2 }} spacingX="44px">
          <Box mb="32px">
            <FormLabel data-cy="cypress-org-name-label">
              Organization name <RequiredAsterisk />
            </FormLabel>
            <InputV2
              name="name"
              value={formik.values.name}
              restInput={{ maxLength: 150 }}
              inputRightElement={<RequiredAsterisk />}
              cypressInputSelector="cypress-org-name"
              cypressErrorSelector="cypress-org-name-error"
            />
          </Box>
          {editMode && (
            <Box mb="32px" style={{ position: "relative", zIndex: 40000 }}>
              <FormLabel>
                Organization type <RequiredAsterisk />
              </FormLabel>
              <SelectV2
                placeholder="Type"
                name="type"
                options={[
                  { label: "Grassroots", value: GRASSROOTS_TYPE },
                  { label: "Non Profit", value: NON_PROFIT_TYPE },
                  { label: "Foundation", value: FOUNDATION_TYPE },
                  { label: "Corporation", value: CORPORATION_TYPE },
                ]}
                value={formik.values.type}
                onChange={(option) =>
                  formik.setFieldValue("type", option.value)
                }
              />
            </Box>
          )}
          <Box mb="32px">
            <FormLabel data-cy="cypress-org-phone-number-label">
              Phone number <RequiredAsterisk />
            </FormLabel>
            <InputV2
              name="phoneNumber"
              restInput={{ maxLength: 150 }}
              value={formik.values.phoneNumber}
              cypressInputSelector="cypress-org-phone-number"
              cypressErrorSelector="cypress-org-phone-number-error"
            />
            <Box mt="8px">
              <Text fontSize="12px" fontWeight="semibold" mb="4px">
                Show phone number on public profile?
              </Text>
              <RadioButtonGroup
                formik={formik}
                name="orgFields.showPhone"
                options={[
                  { label: "Public", value: "true" },
                  { label: "Private", value: "false" },
                ]}
              />
            </Box>
          </Box>
        </SimpleGrid>
      </Box>

      <Box mb="32px">
        <SimpleGrid columns={{ sm: 1, md: 2 }} spacingX="44px">
          <Box mb="32px">
            <FormLabel data-cy="cypress-org-address-label">
              Address <RequiredAsterisk />
            </FormLabel>
            <InputV2
              placeholder="Street number and name"
              name="location.address1"
              restInput={{ maxLength: 150 }}
              value={formik.values.location.address1}
              cypressInputSelector="cypress-org-address"
              cypressErrorSelector="cypress-org-address-error"
            />
            <Box mt="8px">
              <Text fontSize="12px" fontWeight="semibold" mb="4px">
                Show address on public profile?
              </Text>
              <RadioButtonGroup
                formik={formik}
                name="orgFields.showAddress"
                options={[
                  { label: "Public", value: "true" },
                  { label: "Private", value: "false" },
                ]}
              />
            </Box>
          </Box>
          <CitiesAndStates hasMap={true} formik={formik} />
          <Box mb="32px">
            <FormLabel data-cy="cypress-org-zip-code-label">
              Zip code <RequiredAsterisk />
            </FormLabel>
            <InputV2
              placeholder="Zip code"
              name="location.zip"
              restInput={{ maxLength: 150 }}
              value={formik.values.location.zip}
              cypressInputSelector="cypress-org-zip-code"
              cypressErrorSelector="cypress-org-zip-code-error"
            />
          </Box>
        </SimpleGrid>
      </Box>

      {showFundedBy && (
        <Box mb="32px" gridColumn={{ sm: "1 / -1", md: "1 / -1" }}>
          <FormLabel data-cy="cypress-fund-by-label">Funded By</FormLabel>
          {isLoadingFoundations ? (
            <div>Loading foundations...</div>
          ) : (
            <SelectV2
              placeholder={
                formik.values.fundBy === null
                  ? "Select a foundation"
                  : "Edit foundation"
              }
              name="fundBy"
              options={foundationOptions}
              value={formik.values.fundBy || null}
              getOptionLabel={(option) => option.value}
              onChange={(option) => {
                formik.setFieldValue("fundBy", option?.value || null);
              }}
              isClearable
              isDisabled={isLoadingFoundations}
              restSelect={{
                menuPortalTarget: document.body,
                menuPosition: "fixed",
                styles: {
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
              }}
            />
          )}
        </Box>
      )}
      <SimpleGrid columns={[2]} spacingX="44px">
        {(type === GRASSROOTS_TYPE || type === NON_PROFIT_TYPE) && (
          <Box mb="32px">
            <Text
              fontSize="14px"
              fontWeight="semibold"
              color="#000"
              mb="12px"
              data-cy="cypress-org-are-you-care"
            >
              Are you Measure Care Model certified?
            </Text>
            <RadioButtonGroup
              formik={formik}
              name="orgFields.careModelCertified"
              options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
              ]}
            />
          </Box>
        )}
        {(type === NON_PROFIT_TYPE || type === FOUNDATION_TYPE) && (
          <Box mb="32px">
            <FormLabel>
              EIN Number <RequiredAsterisk />
            </FormLabel>
            <InputV2
              placeholder={"EIN Number"}
              name="orgFields.ein"
              restInput={{ maxLength: 150 }}
              cypressInputSelector="cypress-ein-number"
            />
            <Box mt="12px">
              <Checkbox
                isChecked={formik.values.orgFields?.isBlackFundGrantee === true}
                onChange={(e) =>
                  formik.setFieldValue(
                    "orgFields.isBlackFundGrantee",
                    e.target.checked
                  )
                }
                colorScheme="primary"
                data-cy="cypress-org-black-fund-grantee"
              >
                Black Fund grantee (organization receives funding from The Black Fund)
              </Checkbox>
              <Text fontSize="sm" color="gray.500" mt={1}>
                When checked, Black Fund metrics will be available for this organization.
              </Text>
            </Box>
          </Box>
        )}
      </SimpleGrid>
      <Box mb="32px">
        <Text
          fontSize="14px"
          fontWeight="semibold"
          color="#000"
          mb="12px"
          data-cy="cypress-org-lead"
        >
          Who leads your organization? Check all that apply.{" "}
          <RequiredAsterisk />
        </Text>

        <CheckboxGroup
          value={formik.values.labels}
          onChange={(val) => {
            formik.setFieldValue("labels", val);
          }}
        >
          <SimpleGrid columns={{ sm: 1, md: 2 }} spacingX="44px">
            <Box>
              <Box mb="10px">
                <Checkbox
                  key="bipoc-led"
                  value="bipoc-led"
                  colorScheme="primary"
                  data-cy="cypress-checkbox-bipoc-led"
                >
                  BIPOC-led
                </Checkbox>
              </Box>

              <Box mb="10px">
                <Checkbox
                  key="powerful-black-led"
                  value="powerful-black-led"
                  colorScheme="primary"
                  data-cy="cypress-checkbox-powerful-black-led"
                >
                  Powerful Black-Led
                </Checkbox>
              </Box>

              <Box mb="10px">
                <Checkbox
                  key="powerful-brown-led"
                  value="powerful-brown-led"
                  colorScheme="primary"
                  data-cy="cypress-checkbox-brown-black-led"
                >
                  Powerful Brown-Led
                </Checkbox>
              </Box>

              <Box mb="10px">
                <Checkbox
                  key="native-american-or-alaskan-american"
                  value="native-american-or-alaskan-american"
                  colorScheme="primary"
                  data-cy="cypress-checkbox-powerful-indigenous-led"
                >
                  Powerful Indigenous-Led
                </Checkbox>
              </Box>
            </Box>
            <Box>
              <Box mb="10px">
                <Checkbox
                  key="woman"
                  value="woman-led"
                  colorScheme="primary"
                  data-cy="cypress-checkbox-women-led"
                >
                  Woman Led
                </Checkbox>
              </Box>
              <Box mb="10px">
                <Checkbox
                  key="lgbtq"
                  value="lgbtq-led"
                  colorScheme="primary"
                  data-cy="cypress-checkbox-lgbtq-led"
                >
                  LGBTQ-Led
                </Checkbox>
              </Box>

              <Box mb="10px" className="not-listed-field">
                <Flex minWidth="max-content" alignItems="center" gap="2">
                  <Box>
                    <Checkbox
                      key="not-listed"
                      value="not-listed"
                      colorScheme="primary"
                      data-cy="cypress-checkbox-other"
                    >
                      Other:
                    </Checkbox>
                  </Box>

                  <Box flexGrow={1}>
                    <Input
                      isDisabled={
                        !formik.values?.labels?.includes("not-listed")
                      }
                      value={formik.values.labelNotListed}
                      onChange={(val) =>
                        formik.setFieldValue("labelNotListed", val.target.value)
                      }
                      className="flushed"
                      {...{ maxLength: 50 }}
                      name="labelNotListed"
                      data-cy="cypress-other-input"
                    />
                  </Box>
                </Flex>
              </Box>
            </Box>
            <ErrorSpacer>
              <ErrorMessage>{formik.errors["labelNotListed"]}</ErrorMessage>
            </ErrorSpacer>
          </SimpleGrid>
        </CheckboxGroup>
        <Box marginBlockStart={12}>
          <FormLabel data-cy="cypress-org-mission-label">Mission</FormLabel>
          <Textarea
            placeholder="Write your mission..."
            name="mission"
            height={110}
            onChange={(option) =>
              formik.setFieldValue("mission", option.target.value)
            }
            data-cy="cypress-org-mission"
          />
          <ErrorSpacer>
            <ErrorMessage>{formik.errors["mission"]}</ErrorMessage>
          </ErrorSpacer>
        </Box>
        <Box marginBlockStart={12}>
          <FormLabel data-cy="cypress-org-problem-statement-label">
            Problem statement
          </FormLabel>
          <Textarea
            placeholder="Write the problems you are working solve..."
            name="problem"
            onChange={(option) => {
              formik.setFieldValue("problem", option.target.value);
            }}
            height={110}
            value={formik.values.problem}
            data-cy="cypress-org-problem-statement"
          />
          <ErrorSpacer>
            <ErrorMessage>{formik.errors["problem"]}</ErrorMessage>
          </ErrorSpacer>
        </Box>
        <Box marginBlockStart={12}>
          <FormLabel data-cy="cypress-org-vision-label">Vision</FormLabel>
          <Textarea
            name="vision"
            value={formik.values.vision}
            onChange={(option) => {
              formik.setFieldValue("vision", option.target.value);
            }}
            placeholder="write your vision here..."
            height={110}
            data-cy="cypress-org-vision"
          />
          <ErrorSpacer>
            <ErrorMessage>{formik.errors["vision"]}</ErrorMessage>
          </ErrorSpacer>
        </Box>
        <Box marginBlockStart={12}>
          <FormLabel data-cy="cypress-org-anti-racist-statement-label">
            Anti-racist statement
          </FormLabel>
          <Textarea
            name="anti-racist"
            value={formik.values.orgFields.antiRacist}
            onChange={(option) => {
              formik.setFieldValue("orgFields.antiRacist", option.target.value);
            }}
            placeholder="Write your anti-racist statement here..."
            height={110}
            data-cy="cypress-org-anti-racist-statement"
          />
          <ErrorSpacer>
            <ErrorMessage>{formik.errors.orgFields?.antiRacist}</ErrorMessage>
          </ErrorSpacer>
        </Box>

        <Box marginBlockStart={12}>
          <Text
            fontSize="14px"
            fontWeight="semibold"
            color="#000"
            mb="12px"
            data-cy="cypress-checkbox-what-community"
          >
            What communities do you serve? Check all that apply.{" "}
            <RequiredAsterisk />
          </Text>

          <CheckboxGroup
            onChange={(val) => {
              formik.setFieldValue("communitiesServed", val);
            }}
            value={formik.values.communitiesServed}
          >
            <SimpleGrid columns={{ sm: 1, md: 2 }} spacingX="44px">
              <Box>
                <Box mb="10px">
                  <Checkbox
                    key="powerful-black-community"
                    value="powerful-black-community"
                    colorScheme="primary"
                    data-cy="cypress-checkbox-powerful-black-community-2"
                  >
                    Powerful Black Community
                  </Checkbox>
                </Box>

                <Box mb="10px">
                  <Checkbox
                    key="powerful-brown-community"
                    value="powerful-brown-community"
                    colorScheme="primary"
                    data-cy="cypress-checkbox-powerful-brown-community-2"
                  >
                    Powerful Brown Community
                  </Checkbox>
                </Box>

                <Box mb="10px">
                  <Checkbox
                    key="powerful-indigenous-community"
                    value="powerful-indigenous-community"
                    colorScheme="primary"
                    data-cy="cypress-checkbox-powerful-indigenous-community-2"
                  >
                    Powerful Indigenous community
                  </Checkbox>
                </Box>
              </Box>
              <Box>
                <Box mb="10px">
                  <Checkbox
                    key="woman"
                    value="woman"
                    colorScheme="primary"
                    data-cy="cypress-checkbox-women-2"
                  >
                    Woman
                  </Checkbox>
                </Box>
                <Box mb="10px">
                  <Checkbox
                    key="lbgtq"
                    value="lgbtq"
                    colorScheme="primary"
                    data-cy="cypress-checkbox-lgbtq-2"
                  >
                    LGBTQ
                  </Checkbox>
                </Box>
                <Box mb="10px" className="not-listed-field">
                  <Flex minWidth="max-content" alignItems="center" gap="2">
                    <Box>
                      <Checkbox
                        key="not-listed"
                        value="not-listed"
                        colorScheme="primary"
                        data-cy="cypress-checkbox-other-2"
                      >
                        Other:
                      </Checkbox>
                    </Box>

                    <Box flexGrow={1}>
                      <Input
                        data-cy="cypress-checkbox-other-input-2"
                        isDisabled={
                          !formik.values?.communitiesServed?.includes(
                            "not-listed"
                          )
                        }
                        value={formik.values.communityServedNotListed}
                        onChange={(val) =>
                          formik.setFieldValue(
                            "communityServedNotListed",
                            val.target.value
                          )
                        }
                        className="flushed"
                        {...{ maxLength: 50 }}
                        required
                      />
                    </Box>
                  </Flex>
                </Box>
              </Box>
              <ErrorSpacer>
                <ErrorMessage>
                  {formik.errors["communityServedNotListed"]}
                </ErrorMessage>
              </ErrorSpacer>
            </SimpleGrid>
          </CheckboxGroup>
        </Box>
      </Box>
    </div>
  );
};

export default StepGeneralInfo;
